import { Box, Stack } from '@mui/material';
import { Button, IconButton, Typography, colors } from '@sweep-io/sweep-design';
import { GroupMembersTable } from './GroupMembersTable';
import { useSelector } from 'react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { AvatarPicture } from '../../../avatar/AvatarPicture';
import { selectAssignmentGroupById } from '../../../../reducers/assignmentGroupsPageReducer';
import { useAssignmentsApiWithReducer } from '../useAssignmentsApiWithReducer';
import { GroupMembersSkeletonTable } from './GroupMembersSkeletonTable';
import RestrictedTooltip from '../../../common/permissions/RestrictedTooltip';
import usePermission from '../../../common/permissions/usePermission';
import {
  AssignmentGroup,
  AssignmentGroupMember,
  CreditQueueRecord,
} from '../../../../reducers/assignmentGroupTypes';
import { cloneDeep } from 'lodash';
import { SearchInput } from '../../../common/SearchInput';
import { useRunOnce } from '../../../common/useRunOnce';
import { telemetry } from '../../../../telemetry';
import { ManageMembersDialog } from '../group-settings/ManageMembersDialog';
import { GroupSettingsDialog } from '../group-settings/NewGroupSettingsDialog';
import { ChevronLeft } from '@sweep-io/sweep-design/dist/icons';
import { useRoutingContext } from '../../../routing/RoutingContext';
import { TruncatedTextTooltip } from '../../../common/TruncatedTextTooltip';
import { Close as CloseIcon } from '@sweep-io/sweep-design/dist/icons/Close';
import { SEARCH_BOX_WIDTH } from '../../../Automations/helper';
import { useGetSidePadding } from '../../configuration-canvas-panel/useGetSidePadding';
import pluralize from 'pluralize';
import { useSetQueryParamsOnMount } from '../../../../navigation-utils/useSetQueryParamsOnMount';
import { AssignmentGroupsQueryParams } from '../../../routing/Groups';
import { CreditQueueBanner } from './CreditQueueBanner';
import { CreditQueueDialog } from './CreditQueueDialog';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';
import { useAssignmentGroupsApiFacade } from '../../../../apis/facades/useAssignmentGroupsApiFacade';

const SelectedGroup = ({
  onBackClick,
  groupId,
  onClose,
  isDialogLayout,
}: {
  onBackClick: () => void;
  groupId: string;
  onClose?: () => void;
  isDialogLayout?: boolean;
}) => {
  useSetQueryParamsOnMount({ [AssignmentGroupsQueryParams.groupId]: groupId });

  const { addNotification } = useSweepNotifications();
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [creditQueueDialogOpen, setCreditQueueDialogOpen] = useState(false);
  const [creditQueueRecords, setCreditQueueRecords] = useState<CreditQueueRecord[]>([]);
  const { selectedGroupSearch, setSelectedGroupSearch } = useRoutingContext();
  const assignmentGroupData = useSelector(selectAssignmentGroupById(groupId));
  const [isAllowedBtn] = usePermission(['edit:assignment-groups']);
  const sidePadding = useGetSidePadding();
  const { creditQueue: isCreditQueueEnabled } = useFeatureToggle();
  const { get_creditQueue, put_creditQueue } = useAssignmentGroupsApiFacade();

  const closeSettingsDialog = useCallback(() => {
    setSettingsDialogOpen(false);
  }, []);

  const closeMembersDialog = useCallback(() => setMembersDialogOpen(false), []);
  const closeCreditQueueDialog = useCallback(() => setCreditQueueDialogOpen(false), []);

  const { getAssignmentGroupData, saveAssignmentGroupData } = useAssignmentsApiWithReducer();

  // Check if any member has credits
  const hasAnyCredits = useMemo(() => {
    return assignmentGroupData?.members.some((member) => (member.creditCount ?? 0) > 0) ?? false;
  }, [assignmentGroupData?.members]);

  const fetchCreditQueue = useCallback(async () => {
    try {
      const records = await get_creditQueue(groupId);
      setCreditQueueRecords(records);
    } catch (e) {
      telemetry.captureError(e);
    }
  }, [get_creditQueue, groupId]);

  useEffect(() => {
    if (hasAnyCredits && isCreditQueueEnabled) {
      fetchCreditQueue();
    }
  }, [hasAnyCredits, isCreditQueueEnabled, fetchCreditQueue]);

  const upNextName = useMemo(() => {
    if (!assignmentGroupData?.members) return undefined;

    // First priority: first item in local credit queue list
    if (creditQueueRecords.length > 0) {
      const firstInQueue = creditQueueRecords[0];
      const member = assignmentGroupData.members.find((m) => m.userId === firstInQueue.userId);
      if (member) return member.name;
    }

    // Second priority: server's nextCreditQueueMemberId
    if (assignmentGroupData.nextCreditQueueMemberId) {
      const nextMember = assignmentGroupData.members.find(
        (member) => member.userId === assignmentGroupData.nextCreditQueueMemberId,
      );
      if (nextMember) return nextMember.name;
    }

    // Fallback: first member with credits (for when credits just added, queue not fetched yet)
    const memberWithCredits = assignmentGroupData.members.find(
      (member) => (member.creditCount ?? 0) > 0,
    );
    return memberWithCredits?.name;
  }, [
    assignmentGroupData?.members,
    assignmentGroupData?.nextCreditQueueMemberId,
    creditQueueRecords,
  ]);

  // Handle opening credit queue dialog
  const handleOpenCreditQueueDialog = useCallback(async () => {
    setCreditQueueDialogOpen(true);
    await fetchCreditQueue();
  }, [fetchCreditQueue]);

  // Handle saving credit queue changes
  const handleSaveCreditQueue = useCallback(
    async (records: CreditQueueRecord[], deletedSfIds: string[]) => {
      try {
        const recordsToUpdate = records.map((record, index) => ({
          sfId: record.sfId,
          index: index + 1, // 1-based index
        }));
        await put_creditQueue({
          recordsToUpdate: recordsToUpdate,
          recordsToDelete: deletedSfIds,
        });
        setCreditQueueRecords(records);
        // Refresh group data after save
        await getAssignmentGroupData(groupId);
      } catch (e) {
        telemetry.captureError(e);
        addNotification({
          message: 'Error saving credit queue',
          variant: SweepNotificationVariant.Error,
        });
        throw e;
      }
    },
    [put_creditQueue, groupId, getAssignmentGroupData, addNotification],
  );

  const writableAssignmentGroupData = useMemo(
    () => cloneDeep(assignmentGroupData),
    [assignmentGroupData],
  );

  const partialSave = useCallback(
    async (assignmentGroup: Partial<AssignmentGroup>, resetAllMembersLimit?: number) => {
      if (!writableAssignmentGroupData) {
        return;
      }
      try {
        await saveAssignmentGroupData(
          { ...writableAssignmentGroupData, ...assignmentGroup },
          resetAllMembersLimit,
        );
      } catch (e) {
        telemetry.captureError(e);
        addNotification({
          message: 'Error Saving Assignment Group',
          variant: SweepNotificationVariant.Error,
        });
      }
    },
    [addNotification, writableAssignmentGroupData, saveAssignmentGroupData],
  );

  useRunOnce(() => {
    (async function () {
      try {
        await getAssignmentGroupData(groupId);
      } catch (e) {
        telemetry.captureError(e);
        addNotification({
          message: 'Error Loading Assignment Groups',
          variant: SweepNotificationVariant.Error,
        });
      }
    })();
  });

  const additionalSx = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 1,
  };

  return (
    <Stack
      sx={{
        padding: isDialogLayout ? undefined : `0 ${sidePadding}px`,
        gap: 2,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box display="flex" justifyContent="space-between" gap={10} alignItems="center">
        <Box display="flex" gap={1} flex={1} overflow="hidden">
          <Box display="flex" gap={0.5} alignItems="center">
            <IconButton variant="flat" onClick={onBackClick} size="tiny">
              <ChevronLeft size="medium" />
            </IconButton>
            <Box sx={{ width: '40px', height: '40px' }}>
              <AvatarPicture
                dimension={40}
                avatar={writableAssignmentGroupData?.avatar}
                isInactive={false}
              />
            </Box>
          </Box>
          <Stack justifyContent="center" overflow="hidden">
            <Typography variant="h3">{writableAssignmentGroupData?.name}</Typography>
            {writableAssignmentGroupData?.description && (
              <TruncatedTextTooltip variant="body" color={colors.grey[700]}>
                {writableAssignmentGroupData?.description}
              </TruncatedTextTooltip>
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Box>
            <RestrictedTooltip
              to={['edit:assignment-groups']}
              notAllowedTitle={
                'To modify the settings of this assignment group, please contact your admin.'
              }
            >
              <Button
                disabled={!isAllowedBtn}
                onClick={() => setSettingsDialogOpen(true)}
                variant="system"
                size="small"
              >
                Group settings
              </Button>
            </RestrictedTooltip>
          </Box>
          <Box>
            <RestrictedTooltip
              to={['edit:assignment-groups']}
              notAllowedTitle={
                'To modify the settings of this assignment group, please contact your admin.'
              }
            >
              <Button
                disabled={!isAllowedBtn}
                onClick={() => setMembersDialogOpen(true)}
                size="small"
              >
                Manage members
              </Button>
            </RestrictedTooltip>
          </Box>
          {isDialogLayout && (
            <IconButton variant="flat" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          position: 'relative',
          pointerEvents: 'none',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {!writableAssignmentGroupData && <GroupMembersSkeletonTable />}
        {writableAssignmentGroupData && (
          <>
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              height="100%"
              sx={{ pointerEvents: 'all' }}
            >
              <Box sx={additionalSx}>
                <Typography variant="body" color={colors.grey[700]}>
                  {pluralize('Group Member', writableAssignmentGroupData?.members.length, true)}
                </Typography>
                <Box width={SEARCH_BOX_WIDTH}>
                  <SearchInput
                    withFixedMagnifyingGlassIcon
                    TextFieldProps={{
                      value: selectedGroupSearch,
                      placeholder: 'Search',
                      onChange: (e) => {
                        setSelectedGroupSearch(e.target.value);
                      },
                    }}
                    onClearButtonClick={() => setSelectedGroupSearch('')}
                    size="small"
                  />
                </Box>
              </Box>
              {isCreditQueueEnabled && hasAnyCredits && (
                <CreditQueueBanner
                  upNextName={upNextName}
                  onCreditQueueClick={handleOpenCreditQueueDialog}
                />
              )}
              {writableAssignmentGroupData && (
                <GroupMembersTable
                  groupData={writableAssignmentGroupData}
                  filterFunc={(member: AssignmentGroupMember) =>
                    member.name.toLowerCase().includes(selectedGroupSearch.toLowerCase())
                  }
                  onClearFilters={() => setSelectedGroupSearch('')}
                  onAddMember={() => setMembersDialogOpen(true)}
                />
              )}
            </Box>

            {writableAssignmentGroupData && membersDialogOpen && (
              <ManageMembersDialog
                assignmentGroup={writableAssignmentGroupData}
                onClose={closeMembersDialog}
                onSave={partialSave}
              />
            )}
            {writableAssignmentGroupData && settingsDialogOpen && (
              <GroupSettingsDialog
                assignmentGroup={writableAssignmentGroupData}
                onClose={closeSettingsDialog}
                onSave={partialSave}
              />
            )}
            {isCreditQueueEnabled && creditQueueDialogOpen && (
              <CreditQueueDialog
                isOpen={creditQueueDialogOpen}
                onClose={closeCreditQueueDialog}
                onSave={handleSaveCreditQueue}
                creditQueueRecords={creditQueueRecords}
                members={writableAssignmentGroupData?.members ?? []}
              />
            )}
          </>
        )}
      </Box>
    </Stack>
  );
};

export default SelectedGroup;
